import { create } from 'zustand';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BoardItem {
  mondayItemId: number;
  boardId?: number;
  boardName?: string;
  name: string;
  groupId: string;
  groupName: string;
  groupColor?: string;
  status?: string;
  statusColor?: string;
  assigneeIds: number[];
  assigneeNames: string[];
  startDate?: string;
  endDate?: string;
  color?: string;
  isPlanned: boolean;
  /** Parsed subitems for this item (only on parent items) */
  subitems?: BoardItem[];
  /** True if this item is a subitem */
  isSubitem?: boolean;
  /** monday.com ID of the parent item (only on subitems) */
  parentItemId?: number;
  /** The subitems board ID on monday.com (for write-back operations on subitems) */
  subitemsBoardId?: number;
}

export interface ItemSearchFilters {
  searchQuery: string;
  assigneeIds: number[];
  statuses: string[];
  groupIds: string[];
  boardIds: number[];
  plannedFilter: 'all' | 'planned' | 'unplanned';
}

/** Stable filter options derived from the initial (unfiltered) load */
export interface FilterOptionsSnapshot {
  assignees: Array<{ id: number; name: string }>;
  statuses: string[];
  /** Maps status label → monday.com index for query_params filtering */
  statusIndexMap?: Record<string, string>;
  groups: Array<{ id: string; name: string }>;
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface ItemSearchState {
  items: BoardItem[];
  /** Stable filter options from the initial unfiltered load — not narrowed by active filters */
  filterOptionsSnapshot: FilterOptionsSnapshot | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  isOpen: boolean;
  filters: ItemSearchFilters;
  autoSaveFilter: boolean;
  /** Cursor for loading more items (null = no more pages) */
  cursor: string | null;

  // Actions
  setItems: (items: BoardItem[]) => void;
  appendItems: (items: BoardItem[]) => void;
  setCursor: (cursor: string | null) => void;
  setFilterOptionsSnapshot: (snapshot: FilterOptionsSnapshot) => void;
  setLoading: (loading: boolean) => void;
  setLoadingMore: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setSearchQuery: (query: string) => void;
  setAssigneeFilter: (ids: number[]) => void;
  setStatusFilter: (statuses: string[]) => void;
  setGroupFilter: (groupIds: string[]) => void;
  setBoardFilter: (boardIds: number[]) => void;
  setPlannedFilter: (filter: 'all' | 'planned' | 'unplanned') => void;
  setAutoSaveFilter: (autoSave: boolean) => void;
  clearFilters: () => void;
}

const DEFAULT_FILTERS: ItemSearchFilters = {
  searchQuery: '',
  assigneeIds: [],
  statuses: [],
  groupIds: [],
  boardIds: [],
  plannedFilter: 'all',
};

export const useItemSearchStore = create<ItemSearchState>((set) => ({
  items: [],
  filterOptionsSnapshot: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  isOpen: false,
  filters: { ...DEFAULT_FILTERS },
  autoSaveFilter: false,
  cursor: null,

  setItems: (items) => set({ items }),
  appendItems: (items) => set((state) => ({ items: [...state.items, ...items] })),
  setCursor: (cursor) => set({ cursor }),
  setFilterOptionsSnapshot: (filterOptionsSnapshot) => set({ filterOptionsSnapshot }),
  setLoading: (isLoading) => set({ isLoading }),
  setLoadingMore: (isLoadingMore) => set({ isLoadingMore }),
  setError: (error) => set({ error }),
  setOpen: (isOpen) => set({ isOpen }),
  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),

  setSearchQuery: (searchQuery) =>
    set((state) => ({ filters: { ...state.filters, searchQuery } })),

  setAssigneeFilter: (assigneeIds) =>
    set((state) => ({ filters: { ...state.filters, assigneeIds } })),

  setStatusFilter: (statuses) =>
    set((state) => ({ filters: { ...state.filters, statuses } })),

  setGroupFilter: (groupIds) =>
    set((state) => ({ filters: { ...state.filters, groupIds } })),

  setBoardFilter: (boardIds) =>
    set((state) => ({ filters: { ...state.filters, boardIds } })),

  setPlannedFilter: (plannedFilter) =>
    set((state) => ({ filters: { ...state.filters, plannedFilter } })),

  setAutoSaveFilter: (autoSaveFilter) => set({ autoSaveFilter }),

  clearFilters: () => set({ filters: { ...DEFAULT_FILTERS } }),
}));
